<script setup>
import { requirementGroupKey, normalizeEventDateMD } from './helpers'

const props = defineProps({
  employers: { type: Array, required: true },
  sortAsc: { type: Boolean, required: true },
  selectedKeys: { type: Object, required: true }, // Set
  isSelectAllChecked: { type: Boolean, required: true },
  historicalReminders: { type: Array, default: () => [] },
  flatKeys: { type: Array, default: () => [] }
})

const emits = defineEmits([
  'toggle-sort',
  'toggle-select-all',
  'toggle-row',
  'event-clicked'
])

function onSelectAllChange(e) {
  emits('toggle-select-all', e.target.checked)
}

function onRowCheckboxChange(group, event) {
  const index = props.flatKeys.indexOf(group.key)
  emits('toggle-row', { key: group.key, index, shiftKey: event.shiftKey })
}

function onEventClicked(employer, ev) {
  emits('event-clicked', { employer, event: ev })
}

/**
 * Get formatted sent dates for a specific event and employer.
 * Returns a string like "June 20, 2025" or "June 20, 2025 and June 21, 2025"
 */
function getSentDatesForEvent(employer, ev) {
  if (!props.historicalReminders || props.historicalReminders.length === 0) return null
  
  // Build the requirement key for this event
  const reqKey = requirementGroupKey(ev.body, normalizeEventDateMD(ev.dueDateObj))
  
  // Find all reminders matching this employer + requirement
  const matchingReminders = props.historicalReminders.filter(r => 
    r.employer_id === employer.employerId && r.requirement_key === reqKey
  )
  
  if (matchingReminders.length === 0) return null
  
  // Extract and format dates, sort by date ascending
  const dates = matchingReminders
    .map(r => r.created_at ? new Date(r.created_at) : null)
    .filter(d => d && !isNaN(d))
    .sort((a, b) => a - b)
    .map(d => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }))
  
  // Remove duplicates (same date from same job)
  const uniqueDates = [...new Set(dates)]
  
  if (uniqueDates.length === 0) return null
  if (uniqueDates.length === 1) return uniqueDates[0]
  if (uniqueDates.length === 2) return `${uniqueDates[0]} and ${uniqueDates[1]}`
  
  // 3+ dates: join with commas, last one with "and"
  const allButLast = uniqueDates.slice(0, -1).join(', ')
  return `${allButLast} and ${uniqueDates[uniqueDates.length - 1]}`
}
</script>

<template>
  <table class="table table-sm align-middle mb-2" id="requirementDeadlinesTableEmployers">
    <thead>
      <tr>
        <th style="width: 22%" class="py-2.5">
          <button id="employerSortToggle" class="btn btn-link p-0 text-decoration-none text-16 px-3.5" @click="$emit('toggle-sort')" title="Toggle sort">
            <strong class="text-secondary">Employer</strong>
            <i class="bi ms-1" :class="sortAsc ? 'bi-sort-down' : 'bi-sort-up'" />
          </button>
        </th>
        <th style="width: 15%" class="py-2.5 text-16"><strong class="text-secondary px-3.5">Due Date</strong></th>
        <th class="py-2.5 text-16"><strong class="text-secondary px-3.5">Requirement</strong></th>
        <th class="py-2.5 ps-4" style="width: 15.5%">
          <div class="form-check d-inline-flex align-items-start gap-2">
            <input class="form-check-input" type="checkbox" id="employerReqSelectAll" :checked="isSelectAllChecked" @change="onSelectAllChange">
            <label class="form-check-label" for="employerReqSelectAll">Select All</label>
          </div>
        </th>
      </tr>
    </thead>
    <tbody id="requirementDeadlinesTableBodyEmployers">
      <template v-if="employers.length > 0">
        <template v-for="employer in employers" :key="employer.employerId">
          <template v-for="(group, idx) in employer.groups" :key="employer.employerId + '-' + idx">
            <tr :id="idx === 0 ? `deadlineTableRow-${employer.plan_year_id || employer.id}` : undefined">
              <td v-if="idx === 0" :rowspan="employer.groups.length" class="align-text-top py-2.5 px-4">
                {{ employer.employerName }}
              </td>
              <td class="text-nowrap align-text-top py-2.5 px-4 text-16">{{ group.dueDateStr }}</td>
              <td class="py-2.5 px-4">
                <ul class="mb-0 list-unstyled">
                  <li v-for="ev in group.events" :key="employer.employerId + '-' + ev.id" class="text-16 mb-1">
                    <a href="#" class="text-primary text-decoration-none" @click.prevent="onEventClicked(employer, ev)">
                      <med>{{ ev.name }}</med>
                    </a>
                    <div>
                      <span
                        v-if="getSentDatesForEvent(employer, ev)"
                       class="reminder-sent-badge text-12 mt-1"
                      >
                        Reminder Sent {{ getSentDatesForEvent(employer, ev) }}
                      </span>
                    </div>
                  </li>
                </ul>
              </td>
              <td class="align-text-top ps-4 py-2.5">
                <div class="form-check d-inline-flex gap-2">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :id="'rem-'+employer.employerId+'-'+idx"
                    :checked="selectedKeys.has(group.key)"
                    @click="onRowCheckboxChange(group, $event)"
                  >
                  <label class="form-check-label" :for="'rem-'+employer.employerId+'-'+idx">Send Reminder</label>
                </div>
              </td>
            </tr>
          </template>
        </template>
      </template>
      <tr v-else>
        <td colspan="4" class="text-center py-4 text-gray-600">No results</td>
      </tr>
    </tbody>
  </table>
</template>

<style scoped>
.table td, .table th { border: 1px solid #C5CDD4; }
.table tr:first-child th { border-top: none; }

.reminder-sent-badge {
  display: inline-block;
  background-color: var(--green-060);
  color: var(--green-500);
  padding: 4px 8px;
  border-radius: 4px;
}
</style>